import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/ProviderUser';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const PROVIDER_USER_API_REDUCER_KEY = 'provider-users';
export const PROVIDER_USER_PATH = 'provider-users';

export const providerUserApi = createApi({
    reducerPath: PROVIDER_USER_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['ProviderUser', 'ProviderUsers'],
    endpoints: (builder) => ({
        getProviderUserList: builder.query<PaginationResult<Entity.ProviderUser>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ PROVIDER_USER_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['ProviderUsers'],
        }),
        getProviderUser: builder.query<Entity.ProviderUser, Entity.PrimaryProviderUser>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = providerUserNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ PROVIDER_USER_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['ProviderUser'],
        }),
        getProviderUserMultiple: builder.query<Entity.ProviderUser, Entity.PrimaryProviderUser[]>({
            query: (ids) => {
                return ({
                    url: `/${ PROVIDER_USER_PATH }/ids${Entity.PrimaryProviderUser.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['ProviderUsers'],
        }),
        getProviderUserPresentation: builder.query<PaginationResult<Entity.ProviderUser>, Entity.PresentationProviderUserRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ PROVIDER_USER_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['ProviderUsers'],
        }),
        
        insertProviderUser: builder.mutation<Entity.ProviderUser, Entity.CreateProviderUser>({
            query: (toInsert) => {
                return ({
                    url: `/${ PROVIDER_USER_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    providerUserApi.util.upsertQueryData('getProviderUser', Entity.PrimaryProviderUser.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['ProviderUsers'],
        }),
        insertMultipleProviderUser: builder.mutation<Entity.ProviderUser[], Entity.CreateProviderUser[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ PROVIDER_USER_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            providerUserApi.util.upsertQueryData('getProviderUser', Entity.PrimaryProviderUser.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['ProviderUsers'],
        }),
        updateProviderUser: builder.mutation<Entity.ProviderUser, Entity.UpdateEntityProviderUser>({
            query: (toUpdate) => {
                return ({
                    url: `/${ PROVIDER_USER_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['ProviderUsers'],
        }),
        updateMultipleProviderUser: builder.mutation<Entity.ProviderUser[], Entity.UpdateEntityProviderUser[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ PROVIDER_USER_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['ProviderUsers'],
        }),
        deleteProviderUser: builder.mutation<Entity.ProviderUser, Entity.ProviderUser>({
            query: (toDelete) => {
                return ({
                    url: `/${ PROVIDER_USER_PATH }/${Entity.PrimaryProviderUser.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['ProviderUsers'],
        }),
        deleteMultipleProviderUser: builder.mutation<Entity.ProviderUser[], Entity.PrimaryProviderUser[]>({
            query: (ids) => {
                return ({
                    url: `/${ PROVIDER_USER_PATH }/batch${Entity.PrimaryProviderUser.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['ProviderUsers'],
        }),
    }),
})

export const [providerUserNormalizer, providerUserNormalizerMiddleware] = createNormalizationMiddleware(PROVIDER_USER_API_REDUCER_KEY, providerUserApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetProviderUserListQuery,
    useGetProviderUserMultipleQuery,
    useGetProviderUserPresentationQuery,
    useGetProviderUserQuery,
    useInsertProviderUserMutation,
    useInsertMultipleProviderUserMutation,
    useUpdateProviderUserMutation,
    useUpdateMultipleProviderUserMutation,
    useDeleteProviderUserMutation,
    useDeleteMultipleProviderUserMutation,
    
} = providerUserApi
